import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import MovieCard from "@/components/base/MovieCard";
import usePageMeta from "@/hooks/usePageMeta";
import { allWorks, searchWorks, sortWorks, TYPE_LABELS } from "@/lib/works";

const TYPE_FILTERS = [
  { value: "all", label: "全部" },
  { value: "movie", label: "电影" },
  { value: "tv", label: "电视剧" },
];

const HOT_KEYWORDS = ["悬疑", "社会派", "列车", "证词", "环线", "犯罪"];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";
  const [keyword, setKeyword] = useState(query);
  const [typeFilter, setTypeFilter] = useState("all");

  useEffect(() => {
    setKeyword(query);
  }, [query]);

  const matched = useMemo(() => searchWorks(allWorks, query), [query]);
  const results = useMemo(() => {
    const filtered = typeFilter === "all" ? matched : matched.filter((work) => work.type === typeFilter);
    return sortWorks(filtered, "heat");
  }, [matched, typeFilter]);

  const hotWorks = useMemo(() => sortWorks(allWorks, "heat").slice(0, 4), []);

  usePageMeta({
    title: query ? `搜索「${query}」- 痴汉电车影视资料` : "站内搜索 - 痴汉电车影视资料",
    description: query
      ? `痴汉电车站内搜索「${query}」的结果，共找到 ${matched.length} 部相关影视作品，含剧情简介与详细信息。`
      : "在痴汉电车影视档案中搜索作品标题、剧情简介、导演与演员，快速定位想看的电影或电视剧。",
    keywords: query ? `痴汉电车,${query},影视搜索,剧情介绍` : "痴汉电车,影视搜索,剧情介绍",
    suffix: false,
  });

  const submitSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const next = new URLSearchParams(searchParams);
    const value = keyword.trim();
    if (value) {
      next.set("q", value);
    } else {
      next.delete("q");
    }
    setSearchParams(next);
  };

  return (
    <SiteLayout>
      <section className="relative w-full pt-28 md:pt-36 pb-8 md:pb-10 px-4 md:px-8 border-b border-background-200/70">
        <div className="absolute inset-0 bg-gradient-to-b from-primary-900/25 to-transparent pointer-events-none" />
        <div className="relative max-w-[1000px] mx-auto">
          <div className="flex items-center gap-3">
            <span className="w-8 h-[2px] bg-primary-500" />
            <span className="font-label text-[11px] tracking-[0.28em] text-primary-300 uppercase">SEARCH ARCHIVE</span>
          </div>
          <h1 className="mt-3 font-heading text-3xl md:text-4xl font-semibold text-foreground-950">搜索作品与剧情</h1>
          <p className="mt-3 text-sm text-foreground-600">支持在作品标题、剧情简介、导演、演员与剧情标签中模糊匹配。</p>

          <form
            onSubmit={submitSearch}
            className="mt-7 flex flex-col sm:flex-row items-stretch gap-2 rounded-lg border border-background-300/60 bg-background-100/80 p-2"
          >
            <div className="flex flex-1 items-center gap-3 px-3 min-w-0">
              <i className="ri-search-line text-foreground-500" />
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="输入作品名、剧情关键词或演员姓名"
                className="w-full min-w-0 bg-transparent py-3 text-sm md:text-base text-foreground-900 placeholder:text-foreground-500 outline-none"
                aria-label="搜索关键词"
                name="q"
              />
            </div>
            <button
              type="submit"
              className="px-6 py-3 rounded-md bg-primary-500 hover:bg-primary-600 text-foreground-950 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              搜索
            </button>
          </form>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-xs text-foreground-500">热门搜索：</span>
            {HOT_KEYWORDS.map((item) => (
              <Link
                key={item}
                to={`/search?q=${encodeURIComponent(item)}`}
                className="px-3 py-1 rounded-full border border-background-200/80 text-xs text-foreground-600 hover:border-primary-500 hover:text-foreground-950 transition-colors"
              >
                {item}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-8 md:py-12">
        <div className="max-w-[1400px] mx-auto">
          {query ? (
            <>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <p className="text-sm text-foreground-600">
                  关键词 <span className="text-foreground-950 font-medium">「{query}」</span> 共找到{" "}
                  <span className="text-primary-300 font-medium">{matched.length}</span> 部作品
                  {typeFilter !== "all" && (
                    <span className="text-foreground-500">
                      {" "}
                      · 已筛选{TYPE_LABELS[typeFilter]} {results.length} 部
                    </span>
                  )}
                </p>
                <div className="flex items-center gap-1 p-1 rounded-full border border-background-200/70 bg-background-100/70 w-fit">
                  {TYPE_FILTERS.map((item) => (
                    <button
                      key={item.value}
                      type="button"
                      onClick={() => setTypeFilter(item.value)}
                      className={`px-4 py-1.5 rounded-full text-xs transition-colors cursor-pointer whitespace-nowrap ${
                        typeFilter === item.value
                          ? "bg-primary-500 text-foreground-950"
                          : "text-foreground-600 hover:text-foreground-950"
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>

              {results.length > 0 ? (
                <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                  {results.map((work) => (
                    <MovieCard key={work.id} work={work} />
                  ))}
                </div>
              ) : (
                <div className="mt-8 rounded-lg border border-background-200/70 bg-background-100/60 p-6 md:p-10">
                  <div className="max-w-xl">
                    <i className="ri-search-eye-line text-3xl text-background-500" />
                    <h2 className="mt-4 font-heading text-xl text-foreground-950">未找到相关作品，换个关键词试试</h2>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                      可以尝试更短的关键词，例如「悬疑」「列车」「证词」，或直接浏览下方的热门作品。
                    </p>
                  </div>
                  <div className="mt-8">
                    <div className="flex items-center gap-3">
                      <span className="w-8 h-[2px] bg-accent-500" />
                      <h3 className="font-heading text-base text-foreground-900">热门作品推荐</h3>
                    </div>
                    <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                      {hotWorks.map((work) => (
                        <Link
                          key={work.id}
                          to={`/detail/${work.id}`}
                          className="flex items-center gap-3 p-3 rounded-md bg-background-50/70 border border-background-200/70 hover:border-primary-500/70 transition-colors group"
                        >
                          <div className="w-10 h-14 rounded overflow-hidden shrink-0">
                            <img
                              src={work.poster}
                              alt={`${work.title} 海报`}
                              title={`${work.title} 剧情介绍`}
                              className="w-full h-full object-cover object-top"
                            />
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs text-foreground-900 font-medium truncate group-hover:text-primary-300 transition-colors">
                              {work.title}
                            </p>
                            <p className="mt-1 text-[11px] text-foreground-500">
                              {TYPE_LABELS[work.type]} · {work.year}
                            </p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="rounded-lg border border-background-200/70 bg-background-100/50 p-6 md:p-10">
              <div className="flex items-center gap-3">
                <span className="w-8 h-[2px] bg-accent-500" />
                <h2 className="font-heading text-lg text-foreground-950">还没有输入关键词</h2>
              </div>
              <p className="mt-3 text-sm text-foreground-600 max-w-xl">
                在上方输入作品名称或剧情关键词开始检索。也可以先从站内热度最高的几部作品入手。
              </p>
              <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                {hotWorks.map((work) => (
                  <MovieCard key={work.id} work={work} />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </SiteLayout>
  );
}